// Implements: F-UIX-018, F-USG-008
/**
 * What "Distribución" (UIX.6) offers a person and how it reads what they type. Plain data in,
 * plain data out.
 *
 * **Which organizations.** The ones where the server answers `usage_allocation:manage` on the
 * organization scope (the editor's own action, not the consumption report's
 * `usage:read_organization`), then the ones holding a team where it answers `usage:read_team`,
 * read from the session's scopes (never a role name). This only decides what the page OFFERS:
 * the server answers each organization's policy for its reader (the whole view with `can_manage`
 * for its admin, the led teams for a team lead) and refuses anybody else with a `404`.
 *
 * **What is typed** is read exactly: a count as a whole number, an amount in dollars (a point or
 * a comma, up to six decimals) into integer micro-dollars by string arithmetic, never a float.
 * `1,000` and `1.000` are refused as ambiguous rather than read as one dollar (`0.500` is half a
 * dollar: no thousands group follows a lone zero), and the bounds
 * are the server's (100000 generations, 1000000 dollars a month).
 *
 * **Checked before it is sent** (P3): a person limit against the ceiling, a team allocation with
 * the other teams' saved ones against the organization budget. The server checks again on submit
 * and its refusal is what the person reads when the two disagree. Reaching a bound exactly is
 * allowed (`>`), as on the server, and a bound of zero is a bound.
 */

import type { BackofficeScope } from "../../../types/api";
import type { CeilingView, OrganizationPolicyView } from "../../../types/usagePolicy";
import { permits } from "./sections";

export interface DistributionTarget {
  readonly organizationId: string;
  /** The organization's name as the session carries it; `null` when it carries none. */
  readonly name: string | null;
}

/**
 * Each organization whose distribution the person manages, then each one holding a team they read,
 * once, in session order.
 */
export function distributionTargetsOf(scopes: readonly BackofficeScope[]): DistributionTarget[] {
  const managed = scopes.flatMap((scope) =>
    scope.kind === "organization" && scope.id !== null && permits(scope, "usage_allocation:manage") ? [scope.id] : [],
  );
  // A team the session cannot place in an organization has no policy address.
  const led = scopes.flatMap((scope) =>
    scope.kind === "team" && scope.id !== null && permits(scope, "usage:read_team") && scope.organization_id != null
      ? [scope.organization_id]
      : [],
  );
  return [...new Set([...managed, ...led])].map((organizationId) => ({
    organizationId,
    name: scopes.find((scope) => scope.kind === "organization" && scope.id === organizationId)?.name ?? null,
  }));
}

export type Parsed =
  | { readonly kind: "empty" }
  | { readonly kind: "value"; readonly value: number }
  | { readonly kind: "invalid" }
  /** `1,000` or `1.000`: a thousands separator or three decimals; never guessed (es-PE writes both). */
  | { readonly kind: "ambiguous" }
  /** A figure past what a number holds exactly: past every bound, so it reads as out of bounds. */
  | { readonly kind: "tooLarge" };

const EMPTY: Parsed = { kind: "empty" };
const INVALID: Parsed = { kind: "invalid" };
const AMBIGUOUS: Parsed = { kind: "ambiguous" };
const TOO_LARGE: Parsed = { kind: "tooLarge" };

/** The server's bounds on a stored limit (`usage/domain/policy.py`): reaching one is allowed. */
export const MAX_MONTHLY_GENERATIONS = 100_000;
export const MAX_MONTHLY_BUDGET_MICROS = 1_000_000_000_000;
const MICROS_PER_DOLLAR = 1_000_000;
const MICRO_DIGITS = 6;

function value(amount: number): Parsed {
  return Number.isSafeInteger(amount) ? { kind: "value", value: amount } : TOO_LARGE;
}

/** A whole number of generations; empty is "the default". */
export function parseCount(text: string): Parsed {
  const trimmed = text.trim();
  if (trimmed === "") return EMPTY;
  return /^\d+$/.test(trimmed) ? value(Number(trimmed)) : INVALID;
}

/** An amount in dollars, into micro-dollars; empty is "the default" (or "nothing typed"). */
export function parseUsdMicros(text: string): Parsed {
  const trimmed = text.trim();
  if (trimmed === "") return EMPTY;
  // A thousands separator follows one to three digits that do not start with a zero: `0.500` is
  // half a dollar, `1.000` could be one or a thousand and is never guessed.
  if (/^[1-9]\d{0,2}[.,]\d{3}$/.test(trimmed)) return AMBIGUOUS;
  // The whole part is `0` or starts with 1-9: `000,500` or `01.5` is a mistyped figure, not a guess.
  // eslint-disable-next-line security/detect-unsafe-regex -- linear: the optional decimal group starts with `[.,]`, which `\d` cannot match, so no input has two parses.
  const match = /^(0|[1-9]\d*)(?:[.,](\d{1,6}))?$/.exec(trimmed);
  if (match === null) return INVALID;
  const whole = Number(match[1]);
  const fraction = Number((match[2] ?? "").padEnd(MICRO_DIGITS, "0"));
  return value(whole * MICROS_PER_DOLLAR + fraction);
}

/** Micro-dollars as a field shows them: at least two decimals, never a float's noise. */
export function usdInputOf(micros: number | null): string {
  if (micros === null) return "";
  const whole = Math.floor(micros / MICROS_PER_DOLLAR);
  const fraction = String(micros % MICROS_PER_DOLLAR)
    .padStart(MICRO_DIGITS, "0")
    .replace(/0+$/, "")
    .padEnd(2, "0");
  return `${String(whole)}.${fraction}`;
}

export type LimitProblem =
  | "invalidGenerations"
  | "invalidBudget"
  | "ambiguousBudget"
  | "generationsOutOfBounds"
  | "budgetOutOfBounds"
  | "generationsAboveCeiling"
  | "budgetAboveCeiling";

function above(typed: Parsed, bound: number | null | undefined): boolean {
  return typed.kind === "value" && bound !== null && bound !== undefined && typed.value > bound;
}

/** Why a person limit cannot be sent as typed, or `null` when it can. */
export function personLimitProblem(generations: Parsed, budget: Parsed, ceiling: CeilingView | null): LimitProblem | null {
  if (generations.kind === "invalid") return "invalidGenerations";
  if (budget.kind === "invalid") return "invalidBudget";
  if (budget.kind === "ambiguous") return "ambiguousBudget";
  if (generations.kind === "tooLarge") return "generationsOutOfBounds";
  if (budget.kind === "tooLarge") return "budgetOutOfBounds";
  if (above(generations, MAX_MONTHLY_GENERATIONS)) return "generationsOutOfBounds";
  if (above(budget, MAX_MONTHLY_BUDGET_MICROS)) return "budgetOutOfBounds";
  if (above(generations, ceiling?.max_generations_per_person)) return "generationsAboveCeiling";
  if (above(budget, ceiling?.max_budget_micros_per_person)) return "budgetAboveCeiling";
  return null;
}

export type AllocationProblem = "invalid" | "ambiguous" | "required" | "outOfBounds" | "overBudget";

/** The saved allocations of every team, or of every team but `except`. */
export function allocatedMicros(policy: OrganizationPolicyView, except?: string): number {
  return policy.teams.reduce(
    (sum, row) => (row.team_id === except ? sum : sum + (row.monthly_budget_micros ?? 0)),
    0,
  );
}

/** Why a team allocation cannot be sent as typed, or `null` when it can. */
export function teamAllocationProblem(
  amount: Parsed,
  teamId: string,
  policy: OrganizationPolicyView,
): AllocationProblem | null {
  if (amount.kind === "invalid") return "invalid";
  if (amount.kind === "ambiguous") return "ambiguous";
  if (amount.kind === "tooLarge") return "outOfBounds";
  if (amount.kind === "empty") return "required";
  if (amount.value > MAX_MONTHLY_BUDGET_MICROS) return "outOfBounds";
  return allocatedMicros(policy, teamId) + amount.value > policy.organization_budget_micros ? "overBudget" : null;
}

/**
 * The names of the teams the session carries in `organizationId`, by id: how a team lead's view
 * names its teams (the lead may not list the organization's teams).
 */
export function sessionTeamNamesOf(scopes: readonly BackofficeScope[], organizationId: string): ReadonlyMap<string, string> {
  const names = new Map<string, string>();
  for (const scope of scopes) {
    if (scope.kind === "team" && scope.id !== null && scope.name !== null && scope.organization_id === organizationId) {
      names.set(scope.id, scope.name);
    }
  }
  return names;
}
